import {formatters, ga} from "./ext-refs";

/**
 * mask all but the last 4 digits
 * @param str
 * @returns {*}
 */
function maskNumber(str) {
    return (str || '').replace(/\d(?=\d{4})/g, "*");
}

/**
 * This lib
 * @param str
 * @returns {string}
 */
function capitalize(str) {
    return str.substring(0, 1).toUpperCase() + str.substring(1).toLowerCase();
}

/**
 * Convert an action message to label format (strips out colons)
 * @param str
 * @returns {*|String|string}
 */
function toLabel(str) {
    return str && str[str.length - 1] == ':' ? str.substring(0, str.length-1) : str;
}

function camelCase(str) {
    return str.split('-')
        .map(function (item, index) {
            if (index !== 0) {
                return capitalize(item);
            } else {
                return item;
            }
        })
        .join('');
}

function camelCaseObject(obj, idx) {
    let result = {};
    idx = idx || 1;
    idx++;
    if (idx > 10) {
        // too deep
        return obj;
    }
    let keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
        let key = keys[i], value = obj[key],
            camelKey = camelCase(key);
        if (Array.isArray(value)) {
            result[camelKey] = value.map(function (item) {
                return camelCaseObject(item, idx)
            });
        } else if (typeof value === 'object') {
            result[camelCase(key)] = camelCaseObject(value, idx);
        } else {
            result[camelCase(key)] = value;
        }
    }
    return result;
}

function getPtDescriptionByCode(paymentConfig, code) {
    let pt = paymentConfig.paymentTypes.find(pt => pt.code === code);
    return pt ? pt.description : "";
}

function getPmDescriptionByCode(paymentConfig, code) {
    let pm = paymentConfig.paymentMethods.find(pm => pm.code === code);
    return pm ? pm.description : "";
}

function getCardPresentPmDescriptionByCode(code) {
    switch (code) {
        case "VISA":
            return "Visa";
        case "VISA_DEBIT":
            return "Visa Debit";
        case "MC":
            return "Mastercard";
        case "MC_DEBIT":
            return "Mastercard Debit";
        case "AMEX":
            return "American Express";
        case "DISC":
            return "Discover";
        case "DISC_DEBIT":
            return "Discover Debit";
        default:
            return "";
    }
}

function getPmCategoryByPmCode(paymentConfig, code) {
    let pm = paymentConfig.paymentMethods.find(pm => pm.code === code);
    return pm ? pm.categoryCode : "";
}

function getPmCategoryDescriptionByCode(paymentConfig, code) {
    let pm = paymentConfig.paymentMethodCategories.find(cat => cat.code === code);
    return pm ? pm.description : "";
}

function initFieldsForReact(object, ...fieldNames) {
    for (let fieldName of fieldNames) {
        object[fieldName] = object[fieldName] || '';
    }
    return object;
}

const JS_DATE_FORMAT = "MM/DD/YYYY";

/**
 * TODO: Move to a library
 *
 * This logic exists in the back end Java code but since in the one page app we are not
 * going to the back end for each shopping cart modification that means we have to duplicate
 * the logic here.
 * @param payment
 */
function initIncludedItems(payment) {
    let includedItems = [];
    let paymentItems = payment.bundle.paymentItems;
    let totalPaymentAmount = 0;
    paymentItems.forEach((item) => {
        let includedItem = {};
        let included = false;
        let type = item.type;
        let selected = (item.selected === "true" || item.selected === true);
        let paymentAmount = parseFloat(item.paymentAmount) || 0;
        let value = item.value;

        if (type === "CHECKBOX") {
            included = selected && paymentAmount > 0;
            includedItem.paymentAmount = paymentAmount;
            includedItem.value = item.paymentAmount;
            includedItem.displayValue = '$' + formatters.addThousandSeparator(formatters.formatAmount(paymentAmount));
        } else if (type === "CHECKBOX_BOOLEAN") {
            included = selected;
            includedItem.value = item.selected;
        } else if (type === "RADIO") {
            included = paymentAmount > 0;
            includedItem.paymentAmount = paymentAmount;
            includedItem.value = item.paymentAmount;
            includedItem.displayValue = '$' + formatters.addThousandSeparator(formatters.formatAmount(paymentAmount));
        } else if (type === "DROPDOWN") {
            included = value;
            if (value) {
                let optionsList = item.optionsList;
                let displayValue = null;
                optionsList.forEach(() => {
                    if (item.value === value) {
                        displayValue = item.text;
                    }
                });
                includedItem.value = value;
                includedItem.displayValue = displayValue;
            }
        }

        if (included) {
            includedItem.label = item.label;
            includedItem.code = item.code;
            includedItems.push(includedItem);
            totalPaymentAmount += paymentAmount;
        }
    });
    payment.bundle.includedItems = includedItems;
    payment.header.paymentAmount = totalPaymentAmount.toFixed(2);
}

/**
 * Utility method to extract non empty messages based on a prefix and maximum size.
 * @param messages
 * @param prefix
 * @param size
 * @returns {Array<String>}
 */
const mapMessages = (messages, prefix, size) => {
    const indexes = [...Array(size).keys()];
    return indexes.map(idx => messages[`${prefix}${idx + 1}`]).filter(item => !!item);
};

const sendGoogleAnalyticsEvent = (category, action, label, value, fieldsObject) => {
    if (ga && typeof ga === 'function') {
        ga('send', 'event', category, action, label, value, fieldsObject);
    }
}

/**
 * Convert client option to boolean.
 *
 * Sample input -> output:
 *    convertToBoolean(true) => true
 *    convertToBoolean("true") => true
 *    convertToBoolean("Y") => true
 *    convertToBoolean("yes") => true
 *    convertToBoolean("1") => true
 *    convertToBoolean(false) => false
 *    convertToBoolean("N") => false
 *
 * @param {string|boolean} value The value to convert
 * @param {Boolean} [defaultValue=false]
 * @returns {Boolean}
 */
const convertToBoolean = (value, defaultValue = false) => {
    if (typeof value === 'boolean') {
        return value;
    }
    const validTrueValues = ['true', 'y', 'yes', '1'];
    const validFalseValues = ['false', 'n', 'no', '0'];
    const validBooleanValues = validTrueValues.concat(validFalseValues);
    if (value && validBooleanValues.includes(value.toLowerCase())) {
        return validTrueValues.includes(value.toLowerCase());
    } else {
        return defaultValue;
    }
}

export {
    camelCaseObject,
    getPtDescriptionByCode,
    getPmDescriptionByCode,
    getCardPresentPmDescriptionByCode,
    getPmCategoryByPmCode,
    getPmCategoryDescriptionByCode,
    initFieldsForReact,
    JS_DATE_FORMAT,
    mapMessages,
    maskNumber,
    toLabel,
    sendGoogleAnalyticsEvent,
    convertToBoolean
};
